import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { useCreateTestLabel } from "@/hooks/useTestLabels";
import { TestLabel, RawTestLabel } from "@/types/testLabel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type ConfirmDuplicationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  testLabel: TestLabel;
};

const ConfirmDuplicationDialog = ({
  open,
  onOpenChange,
  testLabel,
}: ConfirmDuplicationDialogProps) => {
  const navigate = useNavigate();
  const { trigger: createTestLabel, isMutating } = useCreateTestLabel();
  const [name, setName] = useState(`${testLabel.name}_copy`);

  useEffect(() => {
    if (open) setName(`${testLabel.name}_copy`);
  }, [open, testLabel.name]);

  const onConfirm = async () => {
    try {
      await createTestLabel(
        {
          mask_id: testLabel.mask.id,
          name: name.trim(),
          description: testLabel.description,
          spec_list_id: testLabel.specListId,
          measurement_capability_id: testLabel.measurementCapabilityId,
          linked_spec_ids: testLabel.linkedSpecs?.map((spec) => spec.id) ?? [],
          linked_structure_ids: testLabel.linkedStructures?.map((structure) => structure.id) ?? [],
        },
        {
          onError: (err) => toast.error(`Error: ${err.message}`),
          onSuccess: (duplicatedTestLabel: RawTestLabel) => {
            toast.success(`Successfully duplicated Test Label: "${duplicatedTestLabel.name}"`);
            onOpenChange(false);
            navigate(`/test-labels/${duplicatedTestLabel.id}`);
          },
        },
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Duplicate Test Label</DialogTitle>
          <DialogDescription>Choose a name for the duplicated test label.</DialogDescription>
        </DialogHeader>

        <Input
          aria-label="New test label name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isMutating}>
              Cancel
            </Button>
          </DialogClose>
          <Button type="button" onClick={onConfirm} disabled={isMutating || !name.trim()}>
            {isMutating ? "Duplicating…" : "Confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDuplicationDialog;
